import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import Button from './ui/Button';
import Input from './ui/Input';
import type { Tag } from '../types';

interface TagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tag: Omit<Tag, 'id'>) => void;
  editTag?: Tag;
}

const TagModal: React.FC<TagModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editTag
}) => {
  const [name, setName] = useState(editTag?.name || '');
  const [color, setColor] = useState(editTag?.color || '#4f46e5');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      if (name.length > TAG_NAME_MAX_LENGTH) {
        toast.error(`Tag name must be ${TAG_NAME_MAX_LENGTH} characters or less`);
        return;
      }
      onSave({ name: name.trim(), color });
      setName('');
      setColor('#4f46e5');
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-card border rounded-lg shadow-lg max-w-md w-full p-6"
          >
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-4 top-4"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>

            <h2 className="text-lg font-semibold mb-4">
              {editTag ? 'Edit Tag' : 'Create New Tag'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Tag Name
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., React"
                  maxLength={20}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Tag Color
                </label>
                <div className="flex gap-4">
                  <Input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-16 p-1 h-10"
                  />
                  <div
                    className="flex-1 rounded-md p-2"
                    style={{ backgroundColor: `${color}20`, color }}
                  >
                    Preview: <span className="font-medium">{name || 'Tag Name'}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editTag ? 'Save Changes' : 'Create Tag'}
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TagModal;